import type { ShopSlotView, ShopView } from "@highmarch/app";
import { BalanceData, RARITY_IDS, SHOP_IDS, ShopCurrency, ShopId } from "@highmarch/domain";
import { ItemTile, TileNames, fmtAmount, fmtCountdown, itemTile } from "../deals/dealsModel";

/**
 * The Shops screen's view models (docs/SHOP.md §9.5; Kingshot's Mystery, Arena and VIP shops): pure functions from
 * the app's `ShopView`/`ShopSlotView` to what the screen draws — the bottom tabs, each tile's icon, "Left: n", price,
 * discount badge and state, the Merchant's Refresh button and the restock line. No DOM, no clock (`now` is passed in),
 * no rules: what is in stock, at what price and whether it can be bought are @highmarch/app `Shops`'s answers.
 */

/** A Shops tab: one of the currency shops, or the Trophy Hall (MP-23, ui/trophy/trophyHallPage.ts). */
export type ShopTabId = ShopId | "trophy" | "supplies" | "skins";
export const TROPHY_TAB = "trophy";
/** Shop redesign (2026-10-19): the gem goods (Supply Crates, speed-ups, battle supplies, Pet Treats) and the skins, tabs of the one Shop. */
export const SUPPLIES_TAB = "supplies";
export const SKINS_TAB = "skins";

/** The bottom tabs' names (Kingshot: Mystery · Arena · VIP). */
export const SHOP_TITLES: Readonly<Record<ShopId, string>> = { merchant: "Merchant", event: "Event", vip: "VIP" };
/** Each currency as a price and a sentence name it. */
export const CURRENCY_NAMES: Readonly<Record<ShopCurrency, string>> = { marks: "Marks", gems: "gems" };
/** Art paths (art/interface/…): the tab icons and the currencies' own icons. */
export const SHOP_TAB_ICON = (shop: ShopId): string => `icons/shop_tab_${shop}.png`;
/** A discount this deep or deeper wears the red burst; shallower ones the green (Kingshot's -20 % green, -50 % red). */
export const HOT_OFF_PERCENT = 50;
/** Prices are written in full up to here ("26,572"), then compact ("126K"): a tile holds six digits and the icon. */
const FULL_PRICE_BELOW = 100_000;

/** The subset of @highmarch/app `Shops` the tab strip reads. */
export interface ShopsGate {
  unlocked(shop: ShopId): boolean;
  readonly vipLevel: number;
}

export interface ShopTab {
  readonly id: ShopTabId;
  readonly title: string;
  readonly icon: string;
  /** Open for this player (the Merchant and the Event shop by level, the VIP shop from VIP 1). */
  readonly open: boolean;
  /** Why not yet, as the locked page says it ("Clear level 5 to meet the Merchant"). */
  readonly teaser: string;
}

/** The Trophy Hall's tab, when the Hall is in the build (MP-23): open by its level or the first Trophies. */
export interface TrophyTabGate { readonly open: boolean; readonly icon: string }

/**
 * The three tabs, always in Kingshot's order, then the Trophy Hall (MP-23) when given; a closed one shows its teaser
 * page instead of the stock.
 */
export function shopTabs(shops: ShopsGate, balance: BalanceData, trophy: TrophyTabGate | null = null): ShopTab[] {
  const s = balance.shops;
  const teaser: Record<ShopId, string> = {
    merchant: `Clear level ${s.merchant.unlockAfterLevel} to meet the Mystery Merchant`,
    event: `Clear level ${s.event.unlockAfterLevel} to open the Event shop`,
    vip: "Reach VIP 1 to open the VIP shop",
  };
  const shopsTabs: ShopTab[] = SHOP_IDS.map((id) => ({ id, title: SHOP_TITLES[id], icon: SHOP_TAB_ICON(id), open: shops.unlocked(id), teaser: teaser[id] }));
  // Shop redesign: Merchant · Supplies · Event · VIP · Trophies · Skins, one tab strip along the top of the Shop sheet.
  const tabs: ShopTab[] = [shopsTabs[0], { id: SUPPLIES_TAB, title: "Supplies", icon: "icons/crate@3x.png", open: true, teaser: "" }, ...shopsTabs.slice(1)];
  if (trophy) tabs.push({ id: TROPHY_TAB, title: "Trophies", icon: trophy.icon, open: trophy.open, teaser: `Clear level ${balance.trophies.unlockAfterLevel}, or win your first Trophies, to open the Trophy Hall` });
  tabs.push({ id: SKINS_TAB, title: "Skins", icon: "icons/skins@3x.png", open: true, teaser: "" });
  return tabs;
}

/** Which tab opens: the one asked for (even closed: its teaser answers the tap that asked), else the first open one. */
export function initialShopTab(tabs: readonly ShopTab[], want: ShopTabId | null): ShopTabId {
  if (want && tabs.some((t) => t.id === want)) return want;
  return tabs.find((t) => t.open)?.id ?? SUPPLIES_TAB;
}

/** The Merchant's free item's price (MP-08). */
export const FREE_PRICE = "Free";

/** A price as the tile writes it: "2,500", "26,572", "126K". */
export function priceText(n: number): string {
  return n < FULL_PRICE_BELOW ? Math.round(n).toLocaleString("en-US") : fmtAmount(n);
}

/** What a tile shows under its icon. */
export type SlotState = "ok" | "short" | "sold" | "locked" | "nosave";

export interface SlotTile {
  readonly index: number;
  readonly tile: ItemTile;
  readonly featured: boolean;
  /** "Left: 3" (Kingshot's); the sold-out tile says "Sold out" instead. */
  readonly left: string;
  readonly price: string;
  readonly currency: ShopCurrency;
  readonly state: SlotState;
  /** "-50%" when the discount shows (shops.json showOffFromPercent), else null. */
  readonly badge: string | null;
  readonly hot: boolean;
  /** The locked tile's line ("VIP 4"). */
  readonly lock: string;
  /** MP-08: the Merchant's free item of the day (price "Free", no discount burst). */
  readonly free: boolean;
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** A tile per slot: an offer item's tile (deals' itemTile), or a Merchant gear slot's own piece. */
export function slotTile(balance: BalanceData, level: number, s: ShopSlotView, names: TileNames = {}): SlotTile {
  const state: SlotState = s.blocked === "sold out" ? "sold" : s.locked ? "locked" : s.blocked === "not enough" ? "short" : s.blocked ? "nosave" : "ok";
  return {
    index: s.index, tile: s.gear ? gearTile(balance, s) : itemTile(balance, s.item!, level, -1, names), featured: s.featured,
    left: `Left: ${s.left}`, price: s.free ? FREE_PRICE : priceText(s.price), currency: s.currency, state,
    badge: s.showOff && !s.free ? `-${s.off}%` : null, hot: !s.free && s.off >= HOT_OFF_PERCENT, lock: s.locked ? `VIP ${s.vipLevel}` : "", free: s.free === true,
  };
}

/** A Merchant gear slot (the Trading Post's piece): its slot's painted icon in its rarity frame. */
function gearTile(balance: BalanceData, s: ShopSlotView): ItemTile {
  const g = s.gear!, r = RARITY_IDS[g.rarity] ?? "rare", slot = balance.gear.slots[g.slot];
  return {
    kind: "gear", icon: `ui:gear/gear_${slot?.id ?? "mount"}_${r}.png`, top: "", count: "", tone: r, random: false, needsSlot: false,
    name: `${cap(r)} ${slot?.name ?? "gear"}`, detail: `Lv ${g.level} · unbound: the Trading Post buys it back`,
  };
}

/** The Merchant's Refresh button: its price, "Free (VIP)", or none left today. */
export interface RefreshButton {
  readonly state: "free" | "gems" | "none";
  readonly gems: number;
  readonly label: string;
}

export function refreshButton(v: Pick<ShopView, "refreshCost">): RefreshButton {
  if (v.refreshCost === null) return { state: "none", gems: 0, label: "Come back tomorrow" };
  return v.refreshCost === 0 ? { state: "free", gems: 0, label: "Free · VIP" } : { state: "gems", gems: v.refreshCost, label: priceText(v.refreshCost) };
}

/** The line over the stock: a real countdown to the restock (00:00 UTC; Monday 00:00 UTC for Event and VIP). */
export function restockText(shop: ShopId, endsAt: number, now: number): string {
  return `${shop === "merchant" ? "New stock in" : "Refreshes in:"} ${fmtCountdown(endsAt - now)}`;
}

/** Why a tap on a tile or on Refresh did nothing, as a toast (the app's refusal codes and the tile's `blocked`). */
export function refusalText(code: string, currency: ShopCurrency): string {
  if (/^VIP \d+$/.test(code)) return `Reach ${code} to buy this`;
  switch (code) {
    case "sold out": return "Sold out. Back at the next restock";
    case "not enough": case `not enough ${CURRENCY_NAMES[currency]}`: case "not enough gems": case "not enough Marks":
      return shortHint(currency);
    case "vip level too low": return "Your VIP level is too low for this one";
    case "offer changed": return "The stock just changed: take another look";
    case "no refresh left today": return "No refreshes left today. New stock tomorrow";
    case "not saving here": return "The game is open in another tab";
    case "not here": return "The shops open in the hub";
    case "locked": return "Not open yet";
    default: return "Not right now";
  }
}

/** Where more of a currency comes from (never a store link: Marks are earned). */
export function shortHint(currency: ShopCurrency): string {
  switch (currency) {
    case "marks": return "Not enough Marks: sell spare gear, or win Trials, Heroic levels and farm runs";
    case "gems": return "Not enough gems: stars, bosses and Orders pay more";
  }
}
